/**
 * <price-range-slider> — wraps the existing 2 number inputs (from/to) with a
 * dual-handle visual range slider.
 *
 * Usage:
 *   <price-range-slider data-min="0" data-max="500" data-step="1">
 *     <!-- existing min/max number inputs from collection-filters.liquid -->
 *   </price-range-slider>
 *
 * Behaviour:
 * - Reads min/max bounds from data-min / data-max attributes
 * - Renders 2 thumbs (min handle + max handle) above the existing inputs
 * - Drag → updates the corresponding input → fires native 'input' event so
 *   the parent FacetFiltersForm catches it and submits the form (debounced)
 * - Inversely, if user types into the inputs directly, the thumbs update
 * - Touch-friendly (pointer events)
 */
(function () {
  'use strict';

  class PriceRangeSlider extends HTMLElement {
    constructor() {
      super();
      this.activeThumb = null;
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onInputChange = this.onInputChange.bind(this);
    }

    connectedCallback() {
      this.minInput = this.querySelector('input[name*="price.gte"], input[id$="-from"]');
      this.maxInput = this.querySelector('input[name*="price.lte"], input[id$="-to"]');
      if (!this.minInput || !this.maxInput) return;

      this.min = parseFloat(this.dataset.min || this.minInput.min || '0');
      this.max = parseFloat(this.dataset.max || this.maxInput.max || '0');
      this.step = parseFloat(this.dataset.step || '1');
      if (this.max <= this.min) return;

      this.buildUI();
      this.syncFromInputs();

      // Listen to direct input changes (typed) to keep slider in sync
      this.minInput.addEventListener('input', this.onInputChange);
      this.maxInput.addEventListener('input', this.onInputChange);
    }

    buildUI() {
      const wrap = document.createElement('div');
      wrap.className = 'price-range-slider-track-wrap';
      wrap.innerHTML = `
        <div class="price-range-slider-track" aria-hidden="true">
          <div class="price-range-slider-fill"></div>
        </div>
        <button type="button" class="price-range-slider-thumb price-range-slider-thumb-min"
          aria-label="Min price"
          aria-valuemin="${this.min}" aria-valuemax="${this.max}"
          aria-valuenow="${this.min}"></button>
        <button type="button" class="price-range-slider-thumb price-range-slider-thumb-max"
          aria-label="Max price"
          aria-valuemin="${this.min}" aria-valuemax="${this.max}"
          aria-valuenow="${this.max}"></button>
      `;
      this.insertBefore(wrap, this.firstChild);

      this.trackEl = wrap.querySelector('.price-range-slider-track');
      this.fillEl = wrap.querySelector('.price-range-slider-fill');
      this.thumbMin = wrap.querySelector('.price-range-slider-thumb-min');
      this.thumbMax = wrap.querySelector('.price-range-slider-thumb-max');

      this.thumbMin.addEventListener('pointerdown', this.onPointerDown);
      this.thumbMax.addEventListener('pointerdown', this.onPointerDown);
      this.thumbMin.addEventListener('keydown', this.onThumbKey.bind(this, 'min'));
      this.thumbMax.addEventListener('keydown', this.onThumbKey.bind(this, 'max'));
    }

    syncFromInputs() {
      const minVal = this.parseInput(this.minInput.value, this.min);
      const maxVal = this.parseInput(this.maxInput.value, this.max);
      this.updateThumbPositions(minVal, maxVal);
    }

    parseInput(rawValue, fallback) {
      if (!rawValue) return fallback;
      // Inputs may use comma decimals (currency-aware in collection-filters.liquid)
      const cleaned = String(rawValue).replace(',', '.');
      const num = parseFloat(cleaned);
      return isNaN(num) ? fallback : num;
    }

    updateThumbPositions(minVal, maxVal) {
      const range = this.max - this.min;
      if (range <= 0) return;

      const minPct = Math.max(0, Math.min(100, ((minVal - this.min) / range) * 100));
      const maxPct = Math.max(0, Math.min(100, ((maxVal - this.min) / range) * 100));

      this.thumbMin.style.left = `${minPct}%`;
      this.thumbMax.style.left = `${maxPct}%`;
      this.fillEl.style.left = `${minPct}%`;
      this.fillEl.style.width = `${maxPct - minPct}%`;

      this.thumbMin.setAttribute('aria-valuenow', String(Math.round(minVal)));
      this.thumbMax.setAttribute('aria-valuenow', String(Math.round(maxVal)));
    }

    onInputChange() {
      this.syncFromInputs();
    }

    onPointerDown(event) {
      this.activeThumb = event.currentTarget;
      this.activeThumb.setPointerCapture(event.pointerId);
      this.activeThumb.classList.add('is-active');
      document.addEventListener('pointermove', this.onPointerMove);
      document.addEventListener('pointerup', this.onPointerUp);
      document.addEventListener('pointercancel', this.onPointerUp);
      event.preventDefault();
    }

    onPointerMove(event) {
      if (!this.activeThumb) return;
      const rect = this.trackEl.getBoundingClientRect();
      let pct = ((event.clientX - rect.left) / rect.width) * 100;
      pct = Math.max(0, Math.min(100, pct));
      const value = this.min + (pct / 100) * (this.max - this.min);
      const snapped = this.snapToStep(value);

      const isMin = this.activeThumb.classList.contains('price-range-slider-thumb-min');
      const minVal = this.parseInput(this.minInput.value, this.min);
      const maxVal = this.parseInput(this.maxInput.value, this.max);

      if (isMin) {
        const newMin = Math.min(snapped, maxVal);
        this.minInput.value = newMin;
        this.updateThumbPositions(newMin, maxVal);
      } else {
        const newMax = Math.max(snapped, minVal);
        this.maxInput.value = newMax;
        this.updateThumbPositions(minVal, newMax);
      }
    }

    onPointerUp() {
      if (!this.activeThumb) return;
      this.activeThumb.classList.remove('is-active');
      this.activeThumb = null;
      document.removeEventListener('pointermove', this.onPointerMove);
      document.removeEventListener('pointerup', this.onPointerUp);
      document.removeEventListener('pointercancel', this.onPointerUp);

      // Fire native 'input' on the changed input so FacetFiltersForm picks it up
      this.minInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.maxInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    onThumbKey(which, event) {
      const stepKey = { ArrowLeft: -this.step, ArrowDown: -this.step, ArrowRight: this.step, ArrowUp: this.step };
      const big = { PageDown: -this.step * 10, PageUp: this.step * 10 };
      let delta = stepKey[event.key] ?? big[event.key] ?? 0;
      if (event.key === 'Home') {
        if (which === 'min') {
          this.minInput.value = this.min;
        } else {
          this.maxInput.value = this.parseInput(this.minInput.value, this.min);
        }
      } else if (event.key === 'End') {
        if (which === 'max') {
          this.maxInput.value = this.max;
        } else {
          this.minInput.value = this.parseInput(this.maxInput.value, this.max);
        }
      } else if (delta !== 0) {
        const minVal = this.parseInput(this.minInput.value, this.min);
        const maxVal = this.parseInput(this.maxInput.value, this.max);
        if (which === 'min') {
          this.minInput.value = Math.min(maxVal, Math.max(this.min, minVal + delta));
        } else {
          this.maxInput.value = Math.max(minVal, Math.min(this.max, maxVal + delta));
        }
      } else {
        return;
      }
      event.preventDefault();
      this.syncFromInputs();
      this.minInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.maxInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    snapToStep(value) {
      if (!this.step || this.step <= 0) return value;
      return Math.round(value / this.step) * this.step;
    }
  }

  if (!customElements.get('price-range-slider')) {
    customElements.define('price-range-slider', PriceRangeSlider);
  }
})();
